import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Avatar,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { searchStaff } from 'services/userService';

const debounce = (fn, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

const displayName = (u) =>
  u?.name || `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || u?.username || '—';

// Selector de usuario STAFF (profesor/administrador/etc., no alumnos) por
// nombre/usuario/email, contra datofy-api — devuelve
// { id, username, name, first_name, last_name, email }. Mismo patrón que
// StudentPicker.js pero apuntando a searchStaff() en vez de searchCustomers().
const StaffPicker = ({ value, onSelect, placeholder = 'Buscar por nombre o usuario…' }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const popoverBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const runSearch = useCallback(debounce(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults([]);
      setSearching(false);
      return;
    }
    try {
      const res = await searchStaff(q, { perPage: 8 });
      setResults(res);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, 300), []);

  useEffect(() => {
    if (term.trim()) {
      setSearching(true);
      runSearch(term);
      setOpen(true);
    } else {
      setResults([]);
      setOpen(false);
    }
  }, [term, runSearch]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handlePick = (u) => {
    onSelect({
      id: u.id,
      username: u.username,
      name: displayName(u),
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
    });
    setTerm('');
    setResults([]);
    setOpen(false);
  };

  return (
    <Box position="relative" ref={boxRef}>
      {value ? (
        <HStack
          p={2}
          borderRadius="8px"
          border="1px solid"
          borderColor={borderColor}
          justify="space-between"
        >
          <HStack spacing={2} minW={0}>
            <Avatar size="xs" name={displayName(value)} />
            <VStack align="flex-start" spacing={0} minW={0}>
              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{displayName(value)}</Text>
              {value.email && <Text fontSize="xs" color={mutedColor} noOfLines={1}>{value.email}</Text>}
            </VStack>
          </HStack>
          <Text
            fontSize="xs"
            color="primary.500"
            cursor="pointer"
            fontWeight="semibold"
            onClick={() => onSelect(null)}
          >
            Cambiar
          </Text>
        </HStack>
      ) : (
        <InputGroup size="sm">
          <InputLeftElement>
            <SearchIcon color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input
            borderRadius="8px"
            placeholder={placeholder}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onFocus={() => term.trim() && setOpen(true)}
          />
        </InputGroup>
      )}

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={20}
          bg={popoverBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="8px"
          boxShadow="md"
          maxH="240px"
          overflowY="auto"
        >
          {searching ? (
            <HStack p={3} justify="center">
              <Spinner size="sm" color="primary.500" />
            </HStack>
          ) : results.length === 0 ? (
            <Text fontSize="sm" color={mutedColor} p={3} textAlign="center">
              Sin resultados
            </Text>
          ) : (
            <VStack spacing={0} align="stretch">
              {results.map((u) => (
                <HStack
                  key={u.id}
                  p={2}
                  spacing={2}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => handlePick(u)}
                >
                  <Avatar size="xs" name={displayName(u)} />
                  <VStack align="flex-start" spacing={0} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{displayName(u)}</Text>
                    {u.email && <Text fontSize="xs" color={mutedColor} noOfLines={1}>{u.email}</Text>}
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StaffPicker;
